<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import TeamsAPI from 'dashboard/api/teams';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const { currentAccount, updateAccount, accountScopedRoute } = useAccount();
const inboxes = ref([]);
const agentsByInbox = ref({});
const agentsByTeam = ref({});
const transferRules = ref([]);
const transferTeams = ref([]);
const transferUsers = ref([]);
const enabledVoiceInboxIds = ref([]);
const loading = ref(true);
const savingInboxId = ref(null);
const savingVoice = ref(false);
const savingTransferRules = ref(false);
const voiceInboxes = computed(() =>
  inboxes.value.filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp' && inbox.voice_enabled
  )
);
const defaultQueueConfig = () => ({
  strategy: 'round_robin',
  agent_ids: [],
  ring_timeout_seconds: 20,
  overflow_enabled: false,
  overflow_team_ids: [],
  default_team_id: '',
  team_queues: {},
});

const defaultTeamQueue = () => ({
  strategy: 'round_robin',
  agent_ids: [],
  ring_timeout_seconds: 20,
  overflow_enabled: false,
  overflow_team_ids: [],
});

const normalizeQueueConfig = value => {
  const config = value || {};
  return {
    ...defaultQueueConfig(),
    ...config,
    default_team_id: config.default_team_id
      ? Number(config.default_team_id)
      : '',
    agent_ids: (config.agent_ids || []).map(Number),
    overflow_team_ids: (config.overflow_team_ids || []).map(Number),
    team_queues: Object.fromEntries(
      Object.entries(config.team_queues || {}).map(([teamId, queue]) => [
        teamId,
        {
          ...defaultTeamQueue(),
          ...queue,
          agent_ids: (queue.agent_ids || []).map(Number),
          overflow_team_ids: (queue.overflow_team_ids || []).map(Number),
        },
      ])
    ),
  };
};

const initializeInbox = inbox => {
  inbox.call_queue_config = normalizeQueueConfig(inbox.call_queue_config);
  const attachedIds = new Set(
    (inbox.call_queue_teams || []).map(team => String(team.id))
  );
  inbox.call_queue_config.default_team_id = attachedIds.has(
    String(inbox.call_queue_config.default_team_id)
  )
    ? inbox.call_queue_config.default_team_id
    : '';
  inbox.call_queue_config.overflow_team_ids =
    inbox.call_queue_config.overflow_team_ids.filter(id =>
      attachedIds.has(String(id))
    );
  inbox.call_queue_config.team_queues = Object.fromEntries(
    Object.entries(inbox.call_queue_config.team_queues)
      .filter(([teamId]) => attachedIds.has(teamId))
      .map(([teamId, queue]) => [
        teamId,
        {
          ...queue,
          overflow_team_ids: queue.overflow_team_ids.filter(
            id => attachedIds.has(String(id)) && String(id) !== teamId
          ),
        },
      ])
  );
  (inbox.call_queue_teams || []).forEach(team => {
    const key = String(team.id);
    if (!inbox.call_queue_config.team_queues[key]) {
      inbox.call_queue_config.team_queues[key] = defaultTeamQueue();
    }
  });
};

const teamQueueFor = (inbox, team) =>
  inbox.call_queue_config.team_queues[String(team.id)];

const allTeamAgentIds = (inbox, team) => {
  const members = agentsByTeam.value[`${inbox.id}:${team.id}`] || [];
  const inboxMemberIds = new Set((team.member_ids || []).map(Number));
  return members
    .filter(agent => inboxMemberIds.has(Number(agent.id)))
    .map(agent => Number(agent.id));
};

const selectedTeamAgentIds = (inbox, team) => {
  const configured = teamQueueFor(inbox, team).agent_ids;
  return configured.length ? configured : allTeamAgentIds(inbox, team);
};

const toggleTeamAgent = (inbox, team, agentId, checked) => {
  const queue = teamQueueFor(inbox, team);
  const selected = selectedTeamAgentIds(inbox, team);
  const next = checked
    ? [...selected, Number(agentId)]
    : selected.filter(id => id !== Number(agentId));
  if (!next.length) return;
  queue.agent_ids = [...new Set(next)];
};

const moveId = (ids, id, direction) => {
  const index = ids.indexOf(Number(id));
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) return ids;
  const reordered = [...ids];
  [reordered[index], reordered[nextIndex]] = [
    reordered[nextIndex],
    reordered[index],
  ];
  return reordered;
};

const moveTeamAgent = (inbox, team, agentId, direction) => {
  const queue = teamQueueFor(inbox, team);
  queue.agent_ids = moveId(
    selectedTeamAgentIds(inbox, team),
    agentId,
    direction
  );
};

const moveOverflowTeam = (queue, teamId, direction) => {
  queue.overflow_team_ids = moveId(queue.overflow_team_ids, teamId, direction);
};

const toggleInboxAgent = (inbox, agentId, checked) => {
  const selected = inbox.call_queue_config.agent_ids.length
    ? inbox.call_queue_config.agent_ids
    : (agentsByInbox.value[inbox.id] || []).map(agent => Number(agent.id));
  const next = checked
    ? [...selected, Number(agentId)]
    : selected.filter(id => id !== Number(agentId));
  if (!next.length) return;
  inbox.call_queue_config.agent_ids = [...new Set(next)];
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await InboxesAPI.get();
    inboxes.value = data.payload || [];
    voiceInboxes.value.forEach(initializeInbox);

    await Promise.all(
      voiceInboxes.value.map(async inbox => {
        const requests = [
          InboxesAPI.getAssignableAgents(inbox.id)
            .then(response => {
              agentsByInbox.value[inbox.id] = response.data.payload || [];
            })
            .catch(() => {
              agentsByInbox.value[inbox.id] = [];
            }),
          ...(inbox.call_queue_teams || []).map(async team => {
            try {
              const response = await TeamsAPI.getAgents({ teamId: team.id });
              agentsByTeam.value[`${inbox.id}:${team.id}`] =
                response.data || [];
            } catch {
              agentsByTeam.value[`${inbox.id}:${team.id}`] = [];
            }
          }),
        ];
        await Promise.all(requests);
      })
    );
  } catch {
    useAlert(t('VOICE.ALERTS.QUEUES_LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const loadTransferTargets = async () => {
  const accountId = currentAccount.value?.id;
  if (!accountId) return;

  const savedRules =
    currentAccount.value.custom_attributes?.lumerai_transfer_rules;
  transferRules.value = (Array.isArray(savedRules) ? savedRules : []).map(
    rule => ({
      ...rule,
      target_id: String(rule.target_id || ''),
    })
  );

  try {
    const endpoint = `/api/v1/accounts/${accountId}`;
    const [teamsResponse, usersResponse] = await Promise.all([
      window.axios.get(`${endpoint}/teams`),
      window.axios.get(`${endpoint}/agents`),
    ]);
    if (String(currentAccount.value?.id) !== String(accountId)) return;
    transferTeams.value = teamsResponse.data || [];
    transferUsers.value = usersResponse.data || [];
  } catch {
    useAlert(t('LUMERAI_SETTINGS.ALERTS.TRANSFER_TARGETS_LOAD_ERROR'));
  }
};

const addTransferRule = () => {
  transferRules.value.push({
    id: crypto.randomUUID(),
    criterion: '',
    target_type: 'team',
    target_id: '',
  });
};

const removeTransferRule = index => {
  transferRules.value.splice(index, 1);
};

const saveTransferRules = async () => {
  if (savingTransferRules.value || !currentAccount.value?.id) return;

  savingTransferRules.value = true;
  try {
    const rules = transferRules.value
      .map(rule => ({
        ...rule,
        criterion: rule.criterion?.trim() || '',
        target_type: rule.target_type === 'user' ? 'user' : 'team',
        target_id: String(rule.target_id || ''),
      }))
      .filter(rule => rule.criterion && rule.target_id);

    await updateAccount({
      custom_attributes: {
        ...currentAccount.value.custom_attributes,
        lumerai_transfer_rules: rules,
      },
    });
    transferRules.value = rules;
    useAlert(t('LUMERAI_SETTINGS.ALERTS.AI_SETTINGS_UPDATED'));
  } catch {
    useAlert(t('LUMERAI_SETTINGS.ALERTS.AI_SETTINGS_UPDATE_ERROR'));
  } finally {
    savingTransferRules.value = false;
  }
};

watch(
  () => currentAccount.value?.custom_attributes?.lumerai_voice_call_inboxes,
  inboxIds => {
    enabledVoiceInboxIds.value = (inboxIds || []).map(Number);
  },
  { immediate: true }
);

watch(() => currentAccount.value?.id, loadTransferTargets, { immediate: true });

const toggleVoiceInbox = async (inboxId, enabled) => {
  const id = Number(inboxId);
  const previous = [...enabledVoiceInboxIds.value];
  enabledVoiceInboxIds.value = enabled
    ? [...new Set([...previous, id])]
    : previous.filter(value => value !== id);
  savingVoice.value = true;
  try {
    await updateAccount({
      custom_attributes: {
        ...currentAccount.value.custom_attributes,
        lumerai_voice_call_inboxes: enabledVoiceInboxIds.value,
      },
    });
  } catch {
    enabledVoiceInboxIds.value = previous;
    useAlert(t('LUMERAI_SETTINGS.ALERTS.VOICE_SETTINGS_SAVE_ERROR'));
  } finally {
    savingVoice.value = false;
  }
};

const save = async inbox => {
  savingInboxId.value = inbox.id;
  try {
    const config = inbox.call_queue_config;
    await InboxesAPI.setCallQueue(inbox.id, {
      strategy: config.strategy || 'round_robin',
      agent_ids: config.agent_ids.map(Number),
      ring_timeout_seconds: Number(config.ring_timeout_seconds || 20),
      overflow_enabled: config.overflow_enabled,
      overflow_team_ids: config.overflow_team_ids.map(Number),
      default_team_id: config.default_team_id || null,
      team_queues: config.team_queues,
    });
    useAlert(t('VOICE.ALERTS.QUEUE_SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('VOICE.ALERTS.QUEUE_SAVE_ERROR')
    );
  } finally {
    savingInboxId.value = null;
  }
};

onMounted(load);
</script>

<template>
  <main class="flex-1 min-w-0 overflow-y-auto bg-n-background">
    <div class="mx-auto w-full max-w-[1600px] px-6 py-8 2xl:px-10">
      <header class="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div class="flex items-start gap-4">
          <div
            class="grid place-items-center size-12 rounded-xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-headset text-2xl" />
          </div>
          <div>
            <h1 class="text-2xl font-semibold text-n-slate-12">
              {{ t('VOICE.QUEUES_UI.TITLE') }}
            </h1>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('VOICE.QUEUES_UI.SUBTITLE') }}
            </p>
          </div>
        </div>
        <router-link
          :to="accountScopedRoute('voice_queues_index')"
          class="inline-flex items-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
        >
          <i class="i-lucide-activity" />
          {{ t('VOICE.QUEUES_UI.OPEN_MONITORING') }}
        </router-link>
      </header>

      <div v-if="loading" class="grid place-items-center min-h-48">
        <Spinner />
      </div>

      <template v-else>
        <section
          v-if="voiceInboxes.length"
          class="mb-6 rounded-xl border border-n-weak bg-n-surface-1 p-5"
        >
          <div class="flex items-start gap-3">
            <i class="i-lucide-network text-xl text-n-brand mt-0.5" />
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('VOICE.QUEUES_UI.PBX_TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('VOICE.QUEUES_UI.PBX_DESCRIPTION') }}
              </p>
            </div>
          </div>
        </section>

        <section v-if="voiceInboxes.length" class="grid grid-cols-1 gap-6">
          <article
            v-for="inbox in voiceInboxes"
            :key="inbox.id"
            class="rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden"
          >
            <header
              class="flex flex-wrap items-center justify-between gap-4 border-b border-n-weak p-5"
            >
              <div>
                <h2
                  class="font-semibold text-n-slate-12 flex items-center gap-2"
                >
                  <i class="i-lucide-phone text-n-brand" /> {{ inbox.name }}
                </h2>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ t('VOICE.QUEUES_UI.INBOX_ID', { id: inbox.id }) }}
                  ·
                  {{
                    inbox.inbound_calls_enabled
                      ? t('VOICE.QUEUES_UI.RECEIVING_ACTIVE')
                      : t('VOICE.QUEUES_UI.RECEIVING_DISABLED')
                  }}
                </p>
              </div>
              <label
                class="inline-flex items-center gap-3 rounded-lg border border-n-weak px-4 py-3"
              >
                <span class="text-sm font-medium text-n-slate-12">{{
                  t('VOICE.QUEUES_UI.ENABLE_LUMERAI')
                }}</span>
                <input
                  type="checkbox"
                  :checked="enabledVoiceInboxIds.includes(Number(inbox.id))"
                  :disabled="savingVoice"
                  @change="toggleVoiceInbox(inbox.id, $event.target.checked)"
                />
              </label>
            </header>

            <div class="grid grid-cols-1 gap-5 p-5 xl:grid-cols-2">
              <fieldset
                class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
              >
                <legend class="px-1 text-sm font-semibold text-n-slate-12">
                  {{ t('VOICE.QUEUES_UI.DEFAULT_QUEUE') }}
                </legend>
                <p class="mb-3 text-xs text-n-slate-11">
                  {{ t('VOICE.QUEUES_UI.DEFAULT_QUEUE_HELP') }}
                </p>
                <select
                  v-model="inbox.call_queue_config.default_team_id"
                  class="form-input w-full"
                >
                  <option value="">
                    {{ t('VOICE.QUEUES_UI.GENERAL_QUEUE_OPTION') }}
                  </option>
                  <option
                    v-for="team in inbox.call_queue_teams"
                    :key="team.id"
                    :value="Number(team.id)"
                  >
                    {{ team.name }}
                  </option>
                </select>
                <label class="mt-4 block max-w-sm">
                  <span
                    class="mb-2 block text-sm font-medium text-n-slate-12"
                    >{{ t('VOICE.QUEUES_UI.RING_TIMEOUT') }}</span
                  >
                  <input
                    v-model.number="
                      inbox.call_queue_config.ring_timeout_seconds
                    "
                    type="number"
                    min="10"
                    max="90"
                    class="form-input w-full"
                  />
                </label>
                <label class="mt-4 block max-w-sm">
                  <span
                    class="mb-2 block text-sm font-medium text-n-slate-12"
                    >{{ t('VOICE.QUEUES_UI.GENERAL_STRATEGY') }}</span
                  >
                  <select
                    v-model="inbox.call_queue_config.strategy"
                    class="form-input w-full"
                  >
                    <option value="round_robin">
                      {{ t('VOICE.STRATEGIES.ROUND_ROBIN') }}
                    </option>
                    <option value="assigned_then_fallback">
                      {{ t('VOICE.STRATEGIES.ASSIGNED_FIRST') }}
                    </option>
                    <option value="ring_all">
                      {{ t('VOICE.STRATEGIES.RING_ALL') }}
                    </option>
                  </select>
                </label>
              </fieldset>

              <fieldset
                class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
              >
                <legend class="px-1 text-sm font-semibold text-n-slate-12">
                  {{ t('VOICE.QUEUES_UI.GENERAL_OVERFLOW') }}
                </legend>
                <label
                  class="flex items-center gap-3 text-sm font-medium text-n-slate-12"
                >
                  <input
                    v-model="inbox.call_queue_config.overflow_enabled"
                    type="checkbox"
                  />
                  {{ t('VOICE.QUEUES_UI.ALLOW_OVERFLOW') }}
                </label>
                <p class="my-2 text-xs text-n-slate-11">
                  {{ t('VOICE.QUEUES_UI.OVERFLOW_DESCRIPTION') }}
                </p>
                <div
                  v-if="inbox.call_queue_config.overflow_enabled"
                  class="grid gap-2 sm:grid-cols-2"
                >
                  <label
                    v-for="team in inbox.call_queue_teams"
                    :key="team.id"
                    class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm"
                  >
                    <input
                      v-model="inbox.call_queue_config.overflow_team_ids"
                      type="checkbox"
                      :value="Number(team.id)"
                    />
                    {{ team.name }}
                  </label>
                </div>
                <p
                  v-if="!inbox.call_queue_config.overflow_team_ids.length"
                  class="mt-2 text-xs text-n-slate-10"
                >
                  {{ t('VOICE.QUEUES_UI.NO_OVERFLOW_TEAMS') }}
                </p>
                <ol v-else class="mt-3 space-y-1 text-xs text-n-slate-11">
                  <li
                    v-for="(teamId, index) in inbox.call_queue_config
                      .overflow_team_ids"
                    :key="teamId"
                    class="flex items-center gap-2"
                  >
                    <span>{{
                      t('VOICE.QUEUES_UI.STEP_NUMBER', { number: index + 1 })
                    }}</span>
                    <span class="flex-1">{{
                      inbox.call_queue_teams.find(
                        team => Number(team.id) === Number(teamId)
                      )?.name
                    }}</span>
                    <button
                      type="button"
                      :disabled="index === 0"
                      :aria-label="t('VOICE.QUEUES_UI.MOVE_UP')"
                      @click="
                        moveOverflowTeam(inbox.call_queue_config, teamId, -1)
                      "
                    >
                      <i class="i-lucide-chevron-up" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      :disabled="
                        index ===
                        inbox.call_queue_config.overflow_team_ids.length - 1
                      "
                      :aria-label="t('VOICE.QUEUES_UI.MOVE_DOWN')"
                      @click="
                        moveOverflowTeam(inbox.call_queue_config, teamId, 1)
                      "
                    >
                      <i class="i-lucide-chevron-down" aria-hidden="true" />
                    </button>
                  </li>
                </ol>
              </fieldset>

              <fieldset
                class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 xl:col-span-2"
              >
                <legend class="px-1 text-sm font-semibold text-n-slate-12">
                  {{ t('VOICE.QUEUES_UI.GENERAL_AGENTS') }}
                </legend>
                <p class="mb-3 text-xs text-n-slate-11">
                  {{
                    inbox.call_queue_config.agent_ids.length
                      ? t('VOICE.QUEUES_UI.SELECTED_AGENTS_ONLY')
                      : t('VOICE.QUEUES_UI.ALL_INBOX_AGENTS_ELIGIBLE')
                  }}
                </p>
                <div
                  class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4"
                >
                  <label
                    v-for="agent in agentsByInbox[inbox.id] || []"
                    :key="agent.id"
                    class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      :checked="
                        !inbox.call_queue_config.agent_ids.length ||
                        inbox.call_queue_config.agent_ids.includes(
                          Number(agent.id)
                        )
                      "
                      :disabled="
                        inbox.call_queue_config.agent_ids.length === 1 &&
                        inbox.call_queue_config.agent_ids[0] ===
                          Number(agent.id)
                      "
                      @change="
                        toggleInboxAgent(inbox, agent.id, $event.target.checked)
                      "
                    />
                    <span class="truncate">{{ agent.name }}</span>
                  </label>
                </div>
              </fieldset>
            </div>

            <section class="border-t border-n-weak p-5">
              <div class="mb-4">
                <h3 class="font-semibold text-n-slate-12">
                  {{ t('VOICE.QUEUES_UI.TEAM_QUEUES') }}
                </h3>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ t('VOICE.QUEUES_UI.TEAM_QUEUES_HELP') }}
                </p>
              </div>
              <div
                v-if="inbox.call_queue_teams?.length"
                class="grid grid-cols-1 gap-4 2xl:grid-cols-2"
              >
                <article
                  v-for="team in inbox.call_queue_teams"
                  :key="team.id"
                  class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <header class="mb-4 flex items-center justify-between gap-3">
                    <h4
                      class="font-semibold text-n-slate-12 flex items-center gap-2"
                    >
                      <i class="i-lucide-users text-n-brand" />{{ team.name }}
                    </h4>
                    <span class="text-xs text-n-slate-10">{{
                      t('VOICE.QUEUES_UI.QUEUE_LABEL')
                    }}</span>
                  </header>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label class="text-sm text-n-slate-12">
                      {{ t('VOICE.QUEUES_UI.DISTRIBUTION_LABEL') }}
                      <select
                        v-model="teamQueueFor(inbox, team).strategy"
                        class="form-input mt-1 w-full"
                      >
                        <option value="round_robin">
                          {{ t('VOICE.STRATEGIES.ROUND_ROBIN') }}
                        </option>
                        <option value="prioritized_hunt">
                          {{ t('VOICE.STRATEGIES.PRIORITIZED_HUNT') }}
                        </option>
                        <option value="ring_all">
                          {{ t('VOICE.STRATEGIES.RING_ALL') }}
                        </option>
                      </select>
                    </label>
                    <label class="text-sm text-n-slate-12">
                      {{ t('VOICE.QUEUES_UI.RING_TIMEOUT') }}
                      <input
                        v-model.number="
                          teamQueueFor(inbox, team).ring_timeout_seconds
                        "
                        type="number"
                        min="10"
                        max="90"
                        class="form-input mt-1 w-full"
                      />
                    </label>
                  </div>

                  <div class="mt-4">
                    <h5 class="mb-2 text-sm font-medium text-n-slate-12">
                      {{ t('VOICE.QUEUES_UI.TEAM_AGENTS_ORDER') }}
                    </h5>
                    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <label
                        v-for="agent in agentsByTeam[
                          `${inbox.id}:${team.id}`
                        ] || []"
                        :key="agent.id"
                        class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          :checked="
                            selectedTeamAgentIds(inbox, team).includes(
                              Number(agent.id)
                            )
                          "
                          :disabled="
                            selectedTeamAgentIds(inbox, team).length === 1 &&
                            selectedTeamAgentIds(inbox, team)[0] ===
                              Number(agent.id)
                          "
                          @change="
                            toggleTeamAgent(
                              inbox,
                              team,
                              agent.id,
                              $event.target.checked
                            )
                          "
                        />
                        <span class="min-w-0 flex-1 truncate">{{
                          agent.name
                        }}</span>
                        <button
                          type="button"
                          :disabled="
                            selectedTeamAgentIds(inbox, team).indexOf(
                              Number(agent.id)
                            ) <= 0
                          "
                          :aria-label="t('VOICE.QUEUES_UI.MOVE_UP')"
                          @click.prevent="
                            moveTeamAgent(inbox, team, agent.id, -1)
                          "
                        >
                          <i class="i-lucide-chevron-up" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          :disabled="
                            selectedTeamAgentIds(inbox, team).indexOf(
                              Number(agent.id)
                            ) ===
                            selectedTeamAgentIds(inbox, team).length - 1
                          "
                          :aria-label="t('VOICE.QUEUES_UI.MOVE_DOWN')"
                          @click.prevent="
                            moveTeamAgent(inbox, team, agent.id, 1)
                          "
                        >
                          <i class="i-lucide-chevron-down" aria-hidden="true" />
                        </button>
                      </label>
                    </div>
                    <p
                      v-if="
                        !(agentsByTeam[`${inbox.id}:${team.id}`] || []).length
                      "
                      class="rounded-lg border border-dashed border-n-weak p-3 text-xs text-n-slate-10"
                    >
                      {{ t('VOICE.QUEUES_UI.NO_TEAM_AGENTS') }}
                    </p>
                  </div>

                  <div class="mt-4 border-t border-n-weak pt-4">
                    <label
                      class="flex items-center gap-3 text-sm font-medium text-n-slate-12"
                    >
                      <input
                        v-model="teamQueueFor(inbox, team).overflow_enabled"
                        type="checkbox"
                      />
                      {{ t('VOICE.QUEUES_UI.ALLOW_OVERFLOW') }}
                    </label>
                    <div
                      v-if="teamQueueFor(inbox, team).overflow_enabled"
                      class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2"
                    >
                      <label
                        v-for="overflowTeam in inbox.call_queue_teams.filter(
                          item => Number(item.id) !== Number(team.id)
                        )"
                        :key="overflowTeam.id"
                        class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm"
                      >
                        <input
                          v-model="teamQueueFor(inbox, team).overflow_team_ids"
                          type="checkbox"
                          :value="Number(overflowTeam.id)"
                        />
                        {{ overflowTeam.name }}
                      </label>
                    </div>
                    <ol
                      v-if="teamQueueFor(inbox, team).overflow_team_ids.length"
                      class="mt-3 space-y-1 text-xs text-n-slate-11"
                    >
                      <li
                        v-for="(teamId, index) in teamQueueFor(inbox, team)
                          .overflow_team_ids"
                        :key="teamId"
                        class="flex items-center gap-2"
                      >
                        <span>{{
                          t('VOICE.QUEUES_UI.STEP_NUMBER', {
                            number: index + 1,
                          })
                        }}</span>
                        <span class="flex-1">{{
                          inbox.call_queue_teams.find(
                            item => Number(item.id) === Number(teamId)
                          )?.name
                        }}</span>
                        <button
                          type="button"
                          :disabled="index === 0"
                          :aria-label="t('VOICE.QUEUES_UI.MOVE_UP')"
                          @click="
                            moveOverflowTeam(
                              teamQueueFor(inbox, team),
                              teamId,
                              -1
                            )
                          "
                        >
                          <i class="i-lucide-chevron-up" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          :disabled="
                            index ===
                            teamQueueFor(inbox, team).overflow_team_ids.length -
                              1
                          "
                          :aria-label="t('VOICE.QUEUES_UI.MOVE_DOWN')"
                          @click="
                            moveOverflowTeam(
                              teamQueueFor(inbox, team),
                              teamId,
                              1
                            )
                          "
                        >
                          <i class="i-lucide-chevron-down" aria-hidden="true" />
                        </button>
                      </li>
                    </ol>
                  </div>
                </article>
              </div>
              <p
                v-else
                class="rounded-lg border border-dashed border-n-weak p-5 text-sm text-n-slate-11"
              >
                {{ t('VOICE.QUEUES_UI.NO_TEAMS_ATTACHED') }}
              </p>
            </section>

            <footer
              class="flex justify-end border-t border-n-weak bg-n-alpha-1 px-5 py-4"
            >
              <Button
                :label="t('VOICE.QUEUES_UI.SAVE_QUEUE')"
                icon="i-lucide-save"
                :is-loading="savingInboxId === inbox.id"
                :disabled="savingInboxId === inbox.id"
                @click="save(inbox)"
              />
            </footer>
          </article>
        </section>

        <section
          class="mb-6 rounded-xl border border-n-weak bg-n-surface-1 p-5"
        >
          <div class="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('LUMERAI_SETTINGS.TRANSFERS.TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('LUMERAI_SETTINGS.TRANSFERS.DESCRIPTION') }}
              </p>
            </div>
            <Button
              :label="t('LUMERAI_SETTINGS.TRANSFERS.ADD')"
              color="slate"
              :disabled="savingTransferRules"
              @click="addTransferRule"
            />
          </div>

          <div
            v-if="!transferRules.length"
            class="rounded-xl border border-dashed border-n-weak bg-n-alpha-1 py-8 text-center text-sm text-n-slate-11"
          >
            {{ t('LUMERAI_SETTINGS.TRANSFERS.EMPTY') }}
          </div>

          <article
            v-for="(rule, index) in transferRules"
            :key="rule.id || index"
            class="mb-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div class="mb-4 flex items-center justify-between">
              <h3 class="font-medium text-n-slate-12">
                {{
                  t('LUMERAI_SETTINGS.TRANSFERS.RULE_TITLE', {
                    number: index + 1,
                  })
                }}
              </h3>
              <button
                type="button"
                class="rounded-md p-2 text-red-500 hover:bg-red-50"
                :aria-label="t('LUMERAI_SETTINGS.TRANSFERS.REMOVE_RULE')"
                :disabled="savingTransferRules"
                @click="removeTransferRule(index)"
              >
                <i class="i-lucide-trash" aria-hidden="true" />
              </button>
            </div>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label class="block text-sm text-n-slate-12">
                {{ t('LUMERAI_SETTINGS.TRANSFERS.CRITERION') }}
                <input
                  v-model="rule.criterion"
                  :placeholder="
                    t('LUMERAI_SETTINGS.TRANSFERS.CRITERION_PLACEHOLDER')
                  "
                  :disabled="savingTransferRules"
                  class="form-input mt-1 w-full"
                />
              </label>
              <label class="block text-sm text-n-slate-12">
                {{ t('LUMERAI_SETTINGS.TRANSFERS.DESTINATION') }}
                <select
                  v-model="rule.target_type"
                  class="form-input mt-1 w-full"
                  :disabled="savingTransferRules"
                  @change="rule.target_id = ''"
                >
                  <option value="team">
                    {{ t('LUMERAI_SETTINGS.TRANSFERS.TEAM') }}
                  </option>
                  <option value="user">
                    {{ t('LUMERAI_SETTINGS.TRANSFERS.USER') }}
                  </option>
                </select>
              </label>
              <label class="block text-sm text-n-slate-12">
                {{
                  rule.target_type === 'user'
                    ? t('LUMERAI_SETTINGS.TRANSFERS.USER')
                    : t('LUMERAI_SETTINGS.TRANSFERS.TEAM')
                }}
                <select
                  v-model="rule.target_id"
                  class="form-input mt-1 w-full"
                  :disabled="savingTransferRules"
                >
                  <option value="">
                    {{ t('LUMERAI_SETTINGS.TRANSFERS.SELECT_TARGET') }}
                  </option>
                  <option
                    v-for="target in rule.target_type === 'team'
                      ? transferTeams
                      : transferUsers"
                    :key="target.id"
                    :value="String(target.id)"
                  >
                    {{ target.name || target.available_name }}
                  </option>
                </select>
              </label>
            </div>
          </article>

          <div class="mt-4 flex justify-end">
            <Button
              :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_TRANSFER_RULES')"
              color="blue"
              :is-loading="savingTransferRules"
              :disabled="savingTransferRules"
              @click="saveTransferRules"
            />
          </div>
        </section>

        <section
          v-if="!voiceInboxes.length"
          class="mb-7 rounded-xl border border-dashed border-n-weak bg-n-surface-1 p-10 text-center"
        >
          <i class="i-lucide-phone-off text-3xl text-n-slate-9" />
          <h2 class="mt-3 font-medium text-n-slate-12">
            {{ t('VOICE.QUEUES_UI.NO_VOICE_QUEUES') }}
          </h2>
          <p class="mx-auto mt-1 max-w-xl text-sm text-n-slate-11">
            {{ t('VOICE.QUEUES_UI.NO_QUEUES_DESCRIPTION') }}
          </p>
        </section>
      </template>
    </div>
  </main>
</template>
